import React, { useEffect, useState } from 'react';
import { PageWrapper } from '../../components/pageWrapper/PageWrapper';
import UserContactDetailsModal from '../../components/modal/userContactDetailsModal/UserContactDetailsModal';
import UserCompanyDetailsModal from '../../components/modal/userCompanyDetailsModal/UserCompanyDetailsModal';
import userCompanyApi from '../../api/UserCompanyApi';
import UserDetails from '../../components/userDetails/UserDetails';
import DataAccess from '../../components/dataAccess/DataAccess';
import ApiKeyDetails from '../../components/apiKeyDetails/ApiKeyDetails';
import apiKeysApi from '../../api/ApiKeysApi';
import { useNavigate } from 'react-router-dom';
import { EXTERNAL_ROUTES, INTERNAL_ROUTES } from '../../static/Urls';
import userContactApi from '../../api/UserContactApi';
import { PrivateCustomerInfoPage } from '../../components/privateCustomerInfoPage/PrivateCustomerInfoPage';
import { sharedValidators } from '@shared/validation/SharedValidators';
import { FetchUserCompanyDetails } from '@shared/api/dto/UserCompanyDetails';
import ApiKey from '@shared/api/dto/ApiKey';
import { HTTP_RESPONSE_STATUS } from '@shared/static/Constants';
import { FetchUserContactDetails } from '@shared/api/dto/UserContactDetails';
import CompanyDetails from '../../components/companyDetails/CompanyDetails';
import { useReportViewEvent } from '../../tracking/hooks';
import { trackPromise } from 'react-promise-tracker';
import SandboxAccountCreation from '../../components/sandboxAccountCreation/SandboxAccountCreation';
import sandboxUserCredApi from '../../api/SandboxUserCredApi';
import { FetchSandboxUser } from '@shared/api/dto/BoxUserDetails';
import { reportActionEvent } from '../../tracking/tracking';

enum Modal {
  CONTACT_DETAILS,
  CONTACT_DETAILS_MANDATORY,
  COMPANY_DETAILS,
  COMPANY_DETAILS_MANDATORY,
  NULL,
}

const HomePage: React.FC = () => {
  useReportViewEvent({
    completePath: 'is24.de.api.self-service-portal',
  });
  const [activeModal, setActiveModal] = useState<Modal>(Modal.NULL);
  const [userContactDetails, setUserContactDetails] = useState<FetchUserContactDetails | undefined>(undefined);
  const [sandboxUserCredentials, setSandboxUserCredentials] = useState<FetchSandboxUser | undefined>(undefined);
  const [reloadFetchUserContactDetails, setReloadFetchUserContactDetails] = useState<boolean>(false);
  const [userCompanyDetails, setUserCompanyDetails] = useState<FetchUserCompanyDetails | undefined>(undefined);
  const [reloadFetchUserCompanyDetails, setReloadFetchUserCompanyDetails] = useState<boolean>(false);
  const [apiKeys, setApiKeys] = useState<ApiKey[] | []>([]);

  const navigate = useNavigate();

  // loads FetchUserContactDetails
  useEffect(() => {
    userContactApi.fetch().then((data) => setUserContactDetails(data));
  }, [reloadFetchUserContactDetails]);

  const fetchSandboxUserCredentials = () => {
    sandboxUserCredApi.fetchSandbox().then((data) => setSandboxUserCredentials(data));
    reportActionEvent({
      completePath: 'is24.de.api.self-service-portal',
      name: 'sandbox_account_created',
    });
  };

  // loads FetchUserCompanyDetails
  useEffect(() => {
    userCompanyApi
      .fetch()
      .then((companyDetails) => setUserCompanyDetails(companyDetails))
      .catch((e) => {
        e !== HTTP_RESPONSE_STATUS.NO_CONTENT && navigate(INTERNAL_ROUTES.ERROR_PATH);
      });
  }, [reloadFetchUserCompanyDetails]);

  // loads ApiKey[]
  useEffect(() => {
    apiKeysApi.fetch().then((apiKeys) => setApiKeys(apiKeys));
  }, []);

  // triggers mandatory modals
  useEffect(() => {
    setActiveModal(Modal.NULL);
    if (!userCompanyDetails) {
      if (!userContactDetails || !sharedValidators.isValidUserContactDetailsMandatoryFields(userContactDetails)) {
        setActiveModal(Modal.CONTACT_DETAILS_MANDATORY);
      } else {
        setActiveModal(Modal.COMPANY_DETAILS_MANDATORY);
      }
    }
  }, [userCompanyDetails, userContactDetails]);

  const onCloseUserContactDetailsModal = (reload: boolean) => {
    setActiveModal(Modal.NULL);
    if (reload) {
      setReloadFetchUserContactDetails(!reloadFetchUserContactDetails);
    }
  };

  const onCloseUserCompanyDetailsModal = (reload: boolean) => {
    setActiveModal(Modal.NULL);
    if (reload) {
      setReloadFetchUserCompanyDetails(!reloadFetchUserCompanyDetails);
    }
  };

  const onCloseUserCompanyDetailsModalMandatory = (reload: boolean) => {
    setActiveModal(Modal.CONTACT_DETAILS_MANDATORY);
    if (reload) {
      setReloadFetchUserCompanyDetails(!reloadFetchUserCompanyDetails);
    }
  };

  return (
    <PageWrapper>
      <>
        {userCompanyDetails && !userCompanyDetails.isCommercial ? (
          <PrivateCustomerInfoPage />
        ) : (
          <div className="grid grid-flex margin-top-m">
            <div className="grid-item five-twelfth">
              <div className="grid grid-flex">
                <CompanyDetails
                  companyDetails={userCompanyDetails}
                  onEdit={() => {
                    setActiveModal(Modal.COMPANY_DETAILS);
                  }}
                  className={'grid-item one-whole'}
                />
                <UserDetails
                  userDetails={userContactDetails}
                  onEdit={() => {
                    setActiveModal(Modal.CONTACT_DETAILS);
                  }}
                  className={'grid-item one-whole'}
                />
                <DataAccess
                  onEdit={() => window.open(EXTERNAL_ROUTES.ACCESS_DATA_PATH, '_blank')}
                  className={'grid-item one-whole'}
                />
                <SandboxAccountCreation
                  sandboxUserDetails={sandboxUserCredentials}
                  onClick={fetchSandboxUserCredentials}
                  className={'grid-item one-whole'}
                />
              </div>
            </div>
            <div className="grid-item seven-twelfth padding-left-m">
              <ApiKeyDetails
                apiKeys={apiKeys}
                onDelete={(apiKey) => {
                  trackPromise(apiKeysApi.delete(apiKey.apiKey)).then(() =>
                    apiKeysApi.fetch().then((apiKeys) => setApiKeys(apiKeys)),
                  );
                }}
                onEdit={() => window.open(EXTERNAL_ROUTES.SYSTEM_REGISTRATION_PATH + '#systemRegistration', '_blank')}
              />
            </div>
          </div>
        )}
        <UserContactDetailsModal
          visible={Modal.CONTACT_DETAILS_MANDATORY === activeModal}
          onClose={onCloseUserContactDetailsModal}
          value={userContactDetails}
          mandatory={Modal.CONTACT_DETAILS_MANDATORY === activeModal}
        />
        <UserCompanyDetailsModal
          visible={Modal.COMPANY_DETAILS_MANDATORY === activeModal}
          onClose={onCloseUserCompanyDetailsModalMandatory}
          value={userCompanyDetails}
          mandatory={Modal.COMPANY_DETAILS_MANDATORY === activeModal}
        />
        <UserContactDetailsModal
          visible={Modal.CONTACT_DETAILS === activeModal}
          onClose={onCloseUserContactDetailsModal}
          value={userContactDetails}
          mandatory={Modal.CONTACT_DETAILS_MANDATORY === activeModal}
        />
        <UserCompanyDetailsModal
          visible={Modal.COMPANY_DETAILS === activeModal}
          onClose={onCloseUserCompanyDetailsModal}
          value={userCompanyDetails}
          mandatory={Modal.COMPANY_DETAILS_MANDATORY === activeModal}
        />
      </>
    </PageWrapper>
  );
};
export default HomePage;
